import api from '../../api/client';
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  DatePicker,
  Table,
  Tag,
  Spin,
  Button,
  Collapse,
  Space,
  Typography,
  message,
  Statistic,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axios from "axios";
import config from "../../config";
import AttendanceSummary from "./AttendanceSummary";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const { Text } = Typography;
const { Panel } = Collapse;

const AttendanceDailyPage = () => {
  const [date, setDate] = useState(dayjs());
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [users, setUsers] = useState([]);

  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    rate: 0,
  });

  // Fetch positions for role/department mapping
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const { data } = await api.get(`/positions`);
        setPositions(data);
      } catch (err) {
        console.error("Failed to fetch positions", err);
      }
    };
    fetchPositions();
  }, [apiUrl]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get(`/employees`);
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch Users", err);
      }
    };
    fetchUsers();
  }, []);

  // Fetch daily attendance when date changes
  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const formattedDate = date.format("YYYY-MM-DD");
      const { data } = await api.get(`/hr/attendance/daily`, {
        params: { date: formattedDate },
      });

      setAttendance(data);

      // Calculate statistics
      const total = users?.length;
      const present = data.filter(
        (r) => r.status?.toLowerCase() === "present",
      ).length;
      const absent = data.filter(
        (r) => r.status?.toLowerCase() === "absent",
      ).length;
      const late = data.filter(
        (r) => r.status?.toLowerCase() === "late",
      ).length;
      const rate = total ? ((present / total) * 100).toFixed(1) : 0;
setStats({ total, present, absent, late, rate });
    } catch (error) {
      message.error("Failed to fetch attendance records");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [date, apiUrl]);

  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Helper: get role name by position id
  const getRoleName = (positionId) => {
    const role = positions.find((p) => Number(p.id) === Number(positionId));
    return role?.name || "—";
  };

  // Helper: get department name
  const getDepartmentName = (departmentId) => {
    const role = positions.find(
      (p) => Number(p.department_id) === Number(departmentId),
    );
    return role?.department_name || "—";
  };

  // Table columns for daily attendance
  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      key: "employee",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {getRoleName(record.position)}
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      render: (deptId) => getDepartmentName(deptId),
      sorter: (a, b) =>
        getDepartmentName(a.department).localeCompare(
          getDepartmentName(b.department),
        ),
    },
    {
      title: "Clock In",
      dataIndex: "clock_in",
      key: "clock_in",
      render: (time) => time || "--:--",
    },
    {
      title: "Clock Out",
      dataIndex: "clock_out",
      key: "clock_out",
      render: (time) => time || "--:--",
    },
    {
      title: "Hours",
      dataIndex: "hours",
      key: "hours",
      render: (hours) => hours || "--",
      sorter: (a, b) => (a.hours || 0) - (b.hours || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "";
        let icon = null;
        let text = status;
        switch (status?.toLowerCase()) {
          case "present":
            color = "green";
            text = "Present";
            icon = <CheckCircleOutlined />;
            break;
          case "absent":
            color = "red";
            text = "Absent";
            icon = <CloseCircleOutlined />;
            break;
          case "late":
            color = "orange";
            text = "Late";
            icon = <WarningOutlined />;
            break;
          default:
            color = "default";
            text = status || "Unknown";
        }
        return (
          <Tag color={color} icon={icon}>
            {text}
          </Tag>
        );
      },
      filters: [
        { text: "Present", value: "present" },
        { text: "Absent", value: "absent" },
        { text: "Late", value: "late" },
      ],
      onFilter: (value, record) => record.status?.toLowerCase() === value,
    },
  ];

  // Export CSV
  const exportToCSV = () => {
    if (attendance.length === 0) {
      message.warning("No data to export");
      return;
    }
    const headers = [
      "Employee",
      "Department",
      "Clock In",
      "Clock Out",
      "Hours",
      "Status",
    ];
    const rows = attendance.map((row) => [
      row.name,
      getDepartmentName(row.department),
      row.clock_in || "--:--",
      row.clock_out || "--:--",
      row.hours || "--",
      row.status || "Unknown",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    saveAs(blob, `attendance_${date.format("YYYY-MM-DD")}.csv`);
    message.success("CSV exported");
  };

  // Export PDF
  const exportToPDF = () => {
    if (attendance.length === 0) {
      message.warning("No data to export");
      return;
    }
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        ["Employee", "Department", "Clock In", "Clock Out", "Hours", "Status"],
      ],
      body: attendance.map((row) => [
        row.name,
        getDepartmentName(row.department),
        row.clock_in || "--:--",
        row.clock_out || "--:--",
        row.hours || "--",
        row.status || "Unknown",
      ]),
    });
    doc.save(`attendance_${date.format("YYYY-MM-DD")}.pdf`);
    message.success("PDF exported");
  };

  return (
    <>
      {/* Date Filter Card */}
      <Card>
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} sm={12} md={6}>
            <Space>
              <CalendarOutlined />
              <DatePicker
                value={date}
                onChange={(d) => d && setDate(d)}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={18}>
            <Space>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchAttendance}
                loading={loading}
              >
                Refresh
              </Button>
              <Tooltip title="Export CSV">
                <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
                  CSV
                </Button>
              </Tooltip>
              <Tooltip title="Export PDF">
                <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
                  PDF
                </Button>
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>
      {/* <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Space>
              <CalendarOutlined />
              <DatePicker
                value={date}
                onChange={(newDate) => newDate && setDate(newDate)}
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchAttendance}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </Card> */}

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} sm={12} md={4}>
          <Card>
            <Statistic
              title="Total Users"
              value={stats.total}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card>
            <Statistic
              title="Present"
              value={stats.present}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card>
            <Statistic
              title="Absent"
              value={stats.absent}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card>
            <Statistic
              title="Late"
              value={stats.late}
              prefix={<WarningOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={5}>
          <Card>
            <Statistic
              title="Attendance Rate"
              value={stats.rate}
              suffix="%"
              precision={1}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Accordion Sections */}
      <Collapse
        defaultActiveKey={["attendanceLog"]}
        style={{ marginTop: 24 }}
        bordered
      >
        {/* Section 1: Attendance Log (Daily) */}
        <Panel header="Attendance Log" key="attendanceLog">
          <Spin spinning={loading}>
            <Table
              size="small"
              columns={columns}
              dataSource={attendance}
              rowKey={(record) => record.user_id || record.id}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              scroll={{ x: "max-content" }}
              bordered
            />
          </Spin>
        </Panel>

        {/* Section 2 + 3: Monthly Summary (Stats + Employee Summary) */}
        <Panel header="Monthly Summary" key="monthlySummary">
          <AttendanceSummary month={date.format("YYYY-MM")} />
        </Panel>
      </Collapse>
    </>
  );
};

export default AttendanceDailyPage;
